import $ = require("jquery");
import _ = require("underscore");
import Mediator = require("../mediator");
import Utils = require("../utils");
import Portal = require("portal-gis");
import Mn = require("backbone.marionette");
import Base = require("./controller");
import View = require("../views/base-view");
import Collection = require("../collections/base-collection");
import CommandController = require("./base/command-controller");

const log = Utils.log;

interface IArgs {
    in?: any;
    out?: any;
}
class Controller extends Base {

    public view: View<{}>;

    public static about = `
Extends controller to provide a generic view control for managing events and rendering commands and nested controls.

Usage Example:

    <CONTROL id="measure-tool" mid="app/controls/view" text="Measurement Tool">
    <COMMANDS>
        <COMMAND id="measure-linear-tool" mid="app/commands/measure-tool" text="Measure Length" type="action">
        <OPTIONS>
            <Values id="type" value="linear" />
            <Values id="geodesic" value="true" />
            <Values id="toggle" value="true" />
            <Values id="symbology" value="measurements" />
            <Values id="radio-group" value="geo-selection" />
            <Values id="event" value="measurement" />
        </OPTIONS>
        </COMMAND>
        <COMMAND id="measure-area-tool" mid="app/commands/measure-tool" text="Measure Area" type="action">
        <OPTIONS>
            <Values id="type" value="area" />
            <Values id="geodesic" value="true" />
            <Values id="toggle" value="true" />
            <Values id="symbology" value="measurements" />
            <Values id="radio-group" value="geo-selection" />
            <Values id="event" value="measurement" />
        </OPTIONS>
        </COMMAND>
        <COMMAND id="measure-clear-tool" mid="app/commands/measure-tool" text="Clear Measurements" type="action">
        <OPTIONS>
            <Values id="stop" value="false" />
            <Values id="clear" value="true" />
        </OPTIONS>
        </COMMAND>
    </COMMANDS>
    <OPTIONS>
        <Values id="default-uom" value="metric" />
        <Values id="geodesic" value="true" />
        <Values id="symbology" value="measurements" />
        <Values id="autoformat" value="false" />
    </OPTIONS>
    </CONTROL>

    `;

    public static options = {
        "css-name": {
            "about": "class name to assign to the view",
        },
        "icon": {
            "about": "commands only - icon to assign to the command",
        },
        "model": {
            "about": "collection to assign to the view",
        },
        "radio-group": {
            "about": "commands only - place command in this radio group",
        },
        "region": {
            "about": "containing region for this view, default is last-ditch-region",
        },
        "template": {
            "about": "template to assign to the view",
        },
        "text": {
            "about": "commands only - button text",
        },
        "title": {
            "about": "buttons only - button title",
        },
        "view": {
            "about": "the type of view to create, default is views/base-view",
        },
    }

    constructor(public configuration: Portal.ControlLeaf, public mediator: Mediator, public args: IArgs = {}) {
        super(configuration, mediator, args);
        if (typeof configuration.type === "undefined") configuration.type = "startup";

        switch (configuration.type) {
            case "startup":
            case "execute":
                this.execute();
        }
    }

    execute(args?: any) {
    }

    destroy() {
        this.view && this.view.destroy();
        super.destroy();
    }

    init(region: Mn.Region, options?: any) {
        this.view || this.createView();
        region.show(this.view, options);
    }

    // experiemental...should this be on the view, its own Events or removed entirely (rely on the meditor)?
    on(eventName: string, cb: Function) {
        return this.view.on(eventName, cb, this.view);
    }

    /**
    Creates a view using this controllers configuration.
    Template defaults to `./templates/id`
    Region defaults to `id-region`.
    Sets up "command" forwarding on the newly created view
     */
    createView() {
        log("creating controller view: ", this.configuration.id);

        let options = _.defaults(this.options, {
            template: false,//`app/templates/${this.configuration.id}`,
            region: `${this.configuration.id}-region`
        });

        Utils.withModule(View, options["view"], ViewClass => {

            Utils.withModule(new Collection<any>(), options["model"], collection => {

                Utils.withModule(false, options["template"], template => {

                    let viewOptions = this.setViewOptions({
                        className: options["css-name"],
                        template: template,
                        collection: collection
                    });

                    this.view = new ViewClass(this, viewOptions);

                    // forward commands directly to mediator
                    this.view.on("command", args => {
                        this.trigger("command", args);
                    });

                    this.broadcast();
                    this.register();
                })
            })
        });

    }

    /**
    some commands may be new, some may need to be refreshed
    */
    registerCommands(commands = this.configuration.Commands) {

        var d = $.Deferred<Portal.CommandLeaf[]>();

        let commandIds = commands.Commands.map(c => c.id);

        Utils.loadCommandModules(commands.Commands.filter(c => !c.disabled && !this.commandControllers[c.id]))
            .done(info => {
                log("commands loaded: ", this.configuration.id, info.modules.map(c => c.mid));

                // create controllers for new commands
                commandIds.filter(id => !this.commandControllers[id]).forEach(id => {
                    let config = commands.Commands.filter(c => c.id === id)[0];
                    let command = new CommandController(this, config, this.mediator);
                    this.commandControllers[id] = command;
                });

                // remove all newly disabled commands
                false && Object.keys(this.commandControllers).filter(id => -1 === commandIds.indexOf(id)).forEach(id => {
                    this.commandControllers[id].remove();
                    this.commandControllers[id] = undefined;
                });

                // render all the commands mentioned in the merge
                commandIds.map(id => this.commandControllers[id]).forEach(command => command.render());

                d.resolve(commands.Commands);
            });

        return d;
    }

    registerEvents(events: Portal.EventsElement) {
        if (this.view) {
            events.Events.forEach(event => {
                this.view.on(event.name, (args) => {
                    this.trigger(event.name, {
                        event: event,
                        viewArgs: args
                    });
                });
            });
        } else {
            console.warn("create the view before registering events");
        }

        // listen for these events on the mediator
        return super.registerEvents(events);
    }

    public setViewOptions(options) {
        return <Rhythm.GIS.Dictionary<any>>options;
    }

    /**
    @fires "view" to broadcast existence of this view, ensure compatible with mediator IViewArgs
    */
    public broadcast() {
        log("broadcasting controller view: ", this.configuration.id);

        this.trigger("view", {
            controller: this,
            control: this.configuration,
            view: this.view
        });
    }

    public addButton(command: Portal.CommandLeaf, container?: JQuery) {
        log("addButton: ", command.id);

        if (!container && this.view) {
            {
                // did the template already define a region for this command?
                container = $(`.${command.id}`, this.view.$el);
                if (!container.length) container = this.view.$el;
            }
            if (this.view["addButton"]) {
                return this.view["addButton"](command, container);
            }
        }

        var options = Utils.asDictionary(command.Options.Values);

        var buttonOptions = <any>{
            text: true,
            icons: false
        };

        Utils.ifOption(options, "icon", icon => {
            buttonOptions.icons = { primary: `ui-icon ${icon}` };
        });

        Utils.ifOption(options, "text", text => buttonOptions.text = "false" !== text);

        let label = command.text || command.id;

        Utils.ifOption(options, "template", template => {
            label = Utils.translate(template);
        });

        let title = options["title"] || label;

        if (0 === label.indexOf("<%=")) {
            label = Utils.translate(label);
        }

        var button = $(`<button class="action-command ${command.id}" title="${title}">${label}</button>`);
        button.button(buttonOptions)
            .click(() => this.trigger("command", {
                button: button,
                command: command,
                view: this.view,
                controller: this
            }));

        Utils.ifOption(options, "css-name", cssName => button.addClass(cssName));

        Utils.ifOption(options, "radio-group", cmdRadioGroup => {

            this.mediator.on("radio-group", (args: {
                out: {
                    group: string;
                    command: Portal.CommandLeaf;
                }
            }) => {
                if (cmdRadioGroup !== args.out.group) return;
                if (command === args.out.command) {
                    button.addClass("clicked");
                } else {
                    button.removeClass("clicked");
                }
            });
        });

        ["#", "."].some(selector => {
            let node = $(selector + command.id, container);
            if (node.length) {
                container = node;
                return true;
            }
        });
        container.append(button);

        return button;
    }

    public addChild(controller: Controller) {
        log("addChild: ", controller.configuration.id);

        if (!controller.view) {
            console.error("controller has no view:", controller.configuration.id);
            return;
        }
        // TODO: this.view should already be rendered (but it's not!)
        this.view._firstRender && this.view.render();
        let regionName = controller.options["region"] || "last-ditch-region";
        let region = this.view.getRegion(regionName);
        if (!region) {
            this.view.$el.append($(`<div class="${regionName}"></div>`));
            region = this.view.getRegion(regionName);
        }
        region.show(controller.view);
    }

}

export = Controller;
